import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { queryService } from "@/api/services/query-service";
import { OrderProcessingStatus } from "@/api/models/api-types";
import { useCheckoutStore } from "../store/checkout-store";
import { useToast } from "@/features/toast";
import { useCart } from "@/features/cart";

/**
 * Sipariş işleme durumunu saniye başı polling yapar.
 * - PROCESSING → loading göster
 * - COMPLETED  → success sayfasına yönlendir
 * - FAILED     → hata göster, ekranı kapat
 */
export const useOrderProcessing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const { loadCart } = useCart();

  const {
    isProcessingScreenOpen,
    processingOrderGroupId,
    closeProcessingScreen,
    close3DSModal,
  } = useCheckoutStore();

  // Navigate yalnızca bir kez tetiklensin
  const hasNavigated = useRef(false);

  const handleCompleted = useCallback(
    (orderGroupId: string) => {
      if (hasNavigated.current) return;
      hasNavigated.current = true;

      closeProcessingScreen();
      close3DSModal();

      // Sepeti temizle (arka planda)
      loadCart().catch(() => {});

      navigate(`/orders/success/${orderGroupId}`, { replace: true });
    },
    [closeProcessingScreen, close3DSModal, loadCart, navigate]
  );

  const handleFailed = useCallback(
    (message?: string) => {
      closeProcessingScreen();
      close3DSModal();
      showError(message || t("checkout.paymentFailed"), { duration: 6000 });
    },
    [closeProcessingScreen, close3DSModal, showError, t]
  );

  // 1 saniyede bir polling — sadece processing screen açıkken
  const { data, isError } = useQuery({
    queryKey: ["checkout-status", processingOrderGroupId],
    queryFn: () => queryService.getCheckoutStatus(processingOrderGroupId!),
    enabled: isProcessingScreenOpen && !!processingOrderGroupId,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      if (status === OrderProcessingStatus.PROCESSING) return 1000;
      return false;
    },
    retry: 2,
    staleTime: 0,
  });

  // Durum değişimlerini işle
  useEffect(() => {
    if (!data) return;

    const status = data?.data?.status;

    if (status === OrderProcessingStatus.COMPLETED) {
      const orderGroupId =
        data?.data?.orderGroupId ?? processingOrderGroupId!;
      handleCompleted(orderGroupId);
    } else if (status === OrderProcessingStatus.FAILED) {
      const failureReason = data?.data?.failureReason;
      handleFailed(failureReason?.message);
    }
  }, [data, processingOrderGroupId, handleCompleted, handleFailed]);

  // API hatası (network vb.)
  useEffect(() => {
    if (isError) {
      handleFailed(t("checkout.statusCheckError"));
    }
  }, [isError, handleFailed, t]);

  // Processing screen kapandığında hasNavigated'ı sıfırla
  useEffect(() => {
    if (!isProcessingScreenOpen) {
      hasNavigated.current = false;
    }
  }, [isProcessingScreenOpen]);

  return {
    isProcessingScreenOpen,
    processingOrderGroupId,
    closeProcessingScreen,
  };
};
